import { useCallback, useEffect, useMemo, useState } from "react";
import type { Row } from "../types";
import { api, ApiError, type AvaliacaoLink, type ElegivelAvaliacao } from "../api";
import { IconClose, IconLink, IconCopy, IconCheck, IconEye } from "../icons";
import RascunhoLinkModal from "./RascunhoLinkModal";
import { ordenarPorNome, ordenarTextos } from "../ordem";

interface Props {
  colaboradores: Row[]; // para sugerir os setores existentes
  onClose: () => void;
}

/** Monta a URL pública completa do link do gestor a partir do token. */
function urlDoToken(token: string): string {
  const base =
    typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/avaliacao/${token}`;
}

/** Cópia de fallback quando a Clipboard API não está disponível (HTTP/permissão). */
function copiarFallback(texto: string): boolean {
  try {
    const ta = document.createElement("textarea");
    ta.value = texto;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Gera um link de avaliação de desempenho para o gestor de um setor. O gestor
 * abre o link (sem login) e preenche as notas dos colaboradores do setor.
 */
export default function GerarLinkAvaliacaoModal({ colaboradores, onClose }: Props) {
  const [departamento, setDepartamento] = useState("");
  const [gestorNome, setGestorNome] = useState("");
  const [gestorEmail, setGestorEmail] = useState("");
  const [periodo, setPeriodo] = useState("");
  const [gerando, setGerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [novoLink, setNovoLink] = useState<AvaliacaoLink | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);
  /** Link cujo preenchimento salvo (rascunho) está aberto para conferir. */
  const [verRascunho, setVerRascunho] = useState<AvaliacaoLink | null>(null);
  const [links, setLinks] = useState<AvaliacaoLink[]>([]);

  /**
   * QUEM ENTRA NO LINK.
   *
   * O setor sozinho não é confiável: o campo "Departamento" do cadastro é texto
   * livre, e foi por ele que gente de outra área acabou na avaliação do
   * Operacional. Aqui o RH vê a lista antes de gerar e desmarca quem não deve
   * entrar — sem depender de o cadastro estar perfeito.
   */
  const [elegiveis, setElegiveis] = useState<ElegivelAvaliacao[]>([]);
  const [marcados, setMarcados] = useState<number[]>([]);
  const [carregandoElegiveis, setCarregandoElegiveis] = useState(false);

  const departamentos = useMemo(() => {
    const set = new Set<string>();
    for (const c of colaboradores) {
      const d = String(c.departamento ?? "").trim();
      if (d) set.add(d);
    }
    return ordenarTextos([...set]);
  }, [colaboradores]);

  /** "Quem entra no link" em ordem alfabética, venha a API como vier. */
  const elegiveisOrdenados = useMemo(() => ordenarPorNome(elegiveis, (c) => c.nome), [elegiveis]);

  // Troca de setor -> recarrega a lista, com todos marcados. O padrão é
  // "todos": desmarcar é a exceção, e quem não mexer em nada tem o mesmo
  // comportamento de antes.
  useEffect(() => {
    const setor = departamento.trim();
    if (!setor) {
      setElegiveis([]);
      setMarcados([]);
      return;
    }
    let vivo = true;
    setCarregandoElegiveis(true);
    api.avaliacaoLinks
      .elegiveis(setor)
      .then((r) => {
        if (!vivo) return;
        setElegiveis(r.colaboradores);
        setMarcados(r.colaboradores.map((c) => c.id));
      })
      .catch(() => {
        if (vivo) {
          setElegiveis([]);
          setMarcados([]);
        }
      })
      .finally(() => {
        if (vivo) setCarregandoElegiveis(false);
      });
    return () => {
      vivo = false;
    };
  }, [departamento]);

  const carregarLinks = useCallback(async () => {
    try {
      setLinks(await api.avaliacaoLinks.listar());
    } catch {
      /* silencioso — a lista é auxiliar */
    }
  }, []);

  useEffect(() => {
    carregarLinks();
  }, [carregarLinks]);

  async function copiar(texto: string) {
    let ok = false;
    try {
      await navigator.clipboard.writeText(texto);
      ok = true;
    } catch {
      ok = copiarFallback(texto);
    }
    if (ok) {
      setCopiado(texto);
      window.setTimeout(() => setCopiado((c) => (c === texto ? null : c)), 2000);
    } else {
      alert("Não foi possível copiar automaticamente. Selecione o link e copie com Ctrl+C.");
    }
  }

  async function gerar() {
    setError(null);
    if (!departamento.trim()) return setError("Informe o setor (departamento).");
    if (!gestorNome.trim()) return setError("Informe o nome do gestor.");
    setGerando(true);
    try {
      const link = await api.avaliacaoLinks.criar({
        departamento: departamento.trim(),
        gestor_nome: gestorNome.trim(),
        gestor_email: gestorEmail.trim() || undefined,
        periodo: periodo.trim() || undefined,
        // Só manda a escolha quando o RH tirou alguém: lista completa =
        // comportamento de sempre (o link vale para o setor inteiro).
        colaborador_ids:
          marcados.length > 0 && marcados.length < elegiveis.length ? marcados : undefined,
      });
      setNovoLink(link);
      setGestorNome("");
      setGestorEmail("");
      await carregarLinks();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao gerar o link");
    } finally {
      setGerando(false);
    }
  }

  const novaUrl = novoLink ? urlDoToken(novoLink.token) : "";

  function mailtoHref(link: AvaliacaoLink): string {
    const url = urlDoToken(link.token);
    const assunto = `Avaliação de desempenho — ${link.departamento}`;
    const corpo =
      `Olá ${link.gestor_nome},\n\n` +
      `Por favor, preencha a avaliação de desempenho da sua equipe (${link.departamento}) ` +
      `pelo link abaixo:\n${url}\n\nObrigado,\nRH SERCAMP`;
    return `mailto:${encodeURIComponent(link.gestor_email ?? "")}?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}`;
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-ia" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconLink size={18} /> Link de avaliação para o gestor
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-grid">
            <label className="field">
              <span>Setor (departamento) *</span>
              <input
                list="deptos-avaliacao"
                value={departamento}
                onChange={(e) => setDepartamento(e.target.value)}
                placeholder="Ex.: Manutenção"
              />
              <datalist id="deptos-avaliacao">
                {departamentos.map((d) => (
                  <option key={d} value={d} />
                ))}
              </datalist>
            </label>
            <label className="field">
              <span>Gestor do setor *</span>
              <input
                value={gestorNome}
                onChange={(e) => setGestorNome(e.target.value)}
                placeholder="Nome do gestor"
              />
            </label>
            <label className="field">
              <span>E-mail do gestor (opcional)</span>
              <input
                type="email"
                value={gestorEmail}
                onChange={(e) => setGestorEmail(e.target.value)}
                placeholder="gestor@empresa.com"
              />
            </label>
            <label className="field">
              <span>Período (opcional)</span>
              <input
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                placeholder="Ex.: 2026 - 1º semestre"
              />
            </label>
          </div>

          {/* ---- Quem entra na avaliação deste gestor ---- */}
          {departamento.trim() !== "" && (
            <div className="aval-escolha">
              <div className="aval-escolha-topo">
                <strong>
                  Quem o gestor vai avaliar{" "}
                  <span className="badge">
                    {marcados.length}/{elegiveis.length}
                  </span>
                </strong>
                {elegiveis.length > 0 && (
                  <div className="aval-escolha-acoes">
                    <button
                      type="button"
                      className="rot-link"
                      onClick={() => setMarcados(elegiveis.map((c) => c.id))}
                    >
                      marcar todos
                    </button>
                    <button type="button" className="rot-link" onClick={() => setMarcados([])}>
                      limpar
                    </button>
                  </div>
                )}
              </div>

              {carregandoElegiveis ? (
                <p className="muted">Carregando quem está ativo neste setor…</p>
              ) : elegiveis.length === 0 ? (
                <p className="muted">
                  Nenhum colaborador ativo com este setor. Confira o campo "Departamento" no
                  cadastro — é ele que define quem entra.
                </p>
              ) : (
                <>
                  <p className="muted aval-escolha-dica">
                    Desmarque quem não é avaliado por este gestor. Quem está sem formulário
                    costuma ser justamente quem ficou no setor errado.
                  </p>
                  <ul className="aval-escolha-lista">
                    {elegiveisOrdenados.map((c) => (
                      <li key={c.id}>
                        <label>
                          <input
                            type="checkbox"
                            checked={marcados.includes(c.id)}
                            onChange={(e) =>
                              setMarcados((prev) =>
                                e.target.checked
                                  ? [...prev, c.id]
                                  : prev.filter((x) => x !== c.id),
                              )
                            }
                          />
                          <span className="aval-escolha-nome">
                            <strong>{c.nome}</strong>
                            <small className="muted">
                              {c.cargo || "sem cargo"} · {c.empresa_nome}
                            </small>
                          </span>
                        </label>
                        {!c.modelo && (
                          <span className="pill pill-warn" title="O cargo não casou com nenhum formulário FO 094">
                            sem formulário
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          <div className="form-grid">
          </div>

          {error && <div className="form-error">{error}</div>}

          <div style={{ marginTop: 10 }}>
            <button type="button" className="btn-primary" onClick={gerar} disabled={gerando}>
              <IconLink size={16} /> {gerando ? "Gerando…" : "Gerar link"}
            </button>
          </div>

          {novoLink && (
            <div className="form-ok" style={{ marginTop: 14 }}>
              <div style={{ marginBottom: 8 }}>
                <IconCheck size={16} /> Link gerado para <strong>{novoLink.gestor_nome}</strong> —{" "}
                {novoLink.departamento}. Envie ao gestor:
              </div>
              <div className="link-copy-row">
                <input className="link-copy-input" readOnly value={novaUrl} onFocus={(e) => e.target.select()} />
                <button type="button" className="btn-secondary" onClick={() => copiar(novaUrl)}>
                  <IconCopy size={15} /> {copiado === novaUrl ? "Copiado!" : "Copiar"}
                </button>
                <a className="btn-secondary" href={mailtoHref(novoLink)}>
                  E-mail
                </a>
              </div>
            </div>
          )}

          {links.length > 0 && (
            <>
              <h4 className="link-list-title">Links gerados</h4>
              <ul className="link-list">
                {links.map((l) => {
                  const url = urlDoToken(l.token);
                  const respondido = l.status === "Respondido";
                  // O gestor pode salvar cada colaborador e voltar depois: até
                  // enviar, o link fica "Pendente" mas já tem gente preenchida.
                  // Sem isto o RH cobraria quem está no meio do trabalho.
                  const emPreenchimento = !respondido && Number(l.rascunhos ?? 0) > 0;
                  return (
                    <li key={l.id} className="link-list-item">
                      <div className="link-list-info">
                        <span className="link-list-dep">
                          {l.departamento} · {l.gestor_nome}
                        </span>
                        <span className="link-list-meta">
                          <span className={`link-badge ${respondido ? "ok" : l.expirado ? "warn" : ""}`}>
                            {respondido
                              ? "Respondido"
                              : l.expirado
                                ? "Expirado"
                                : emPreenchimento
                                  ? "Em preenchimento"
                                  : "Pendente"}
                          </span>{" "}
                          {typeof l.colaboradores === "number" ? `${l.colaboradores} colaborador(es)` : ""}
                          {emPreenchimento
                            ? ` · ${l.rascunhos} salvo(s) sem enviar`
                            : ""}
                        </span>
                      </div>
                      {emPreenchimento && (
                        <button
                          type="button"
                          className="icon-btn icon-btn-folder"
                          title="Ver o que o gestor já preencheu (e fechar por ele)"
                          onClick={() => setVerRascunho(l)}
                        >
                          <IconEye size={16} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="icon-btn"
                        title="Copiar link"
                        onClick={() => copiar(url)}
                      >
                        {copiado === url ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>

      {verRascunho && (
        <RascunhoLinkModal
          link={verRascunho}
          onClose={() => setVerRascunho(null)}
          onEfetivado={() => {
            setVerRascunho(null);
            void carregarLinks();
          }}
        />
      )}
    </div>
  );
}
